import { STATUS } from "@/constants/statuses";
import ReviewEntity from "@apps/reviews/entities/ReviewEntity";
import ReviewsListResponseEntity from "@apps/reviews/entities/ReviewsLIstResponseEntity";
import ReviewsSortingOptionsEntity from "@apps/reviews/entities/ReviewsSortingOptionsEntity";
import ReviewsListResponseSchema from "@apps/reviews/schemas/ReviewsListResponseSchema";
import { applyTransaction } from "@datorama/akita";
import { ApiClientResponse } from "@lib/ApiClient";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { create } from "superstruct";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import ReviewsImagesSliderStore from "../stores/ReviewsImagesSliderStore";

export type GetReviewsParamsDTO = {
    productId?: number;
    offset?: number;
    sortingOptions?: ReviewsSortingOptionsEntity[] | string;
    search?: string;
    onlyShopReviews?: boolean;
    onlyProductReviews?: boolean;
    onlyPinnedReviews?: boolean;
    onlyWithPhotos?: boolean;
    perPage?: number;
};

class ReviewsImagesSliderService {
    constructor(
        protected reviewsImagesSliderStore: ReviewsImagesSliderStore,
        protected readonly authApiClient: ApiClientInterface,
        protected readonly logger: LoggerInterface
    ) {}
    protected getReviewsRequest(
        reqParams?: GetReviewsParamsDTO
    ): Observable<ApiClientResponse<unknown>> {
        this.logger.debug(
            "ReviewsService.getReviewsRequest is started",
            reqParams
        );

        return this.authApiClient.get({
            url: "/review/getReviewList",
            responseType: "json",
            queryParams: reqParams,
        });
    }

    getReviewsImagesSlider(
        reqParams?: GetReviewsParamsDTO
    ): Observable<ReviewsListResponseEntity> {
        this.reviewsImagesSliderStore.setStatus(STATUS.INITIAL_LOADING);

        return this.getReviewsRequest(reqParams).pipe(
            map((v) => create(v.body, ReviewsListResponseSchema)),
            catchError((error: unknown) => {
                this.logger.debug(
                    "ReviewsService.getReviewsImagesSlider is errored",
                    { reqParams, error }
                );
                applyTransaction(() => {
                    this.reviewsImagesSliderStore.setError(error);
                    this.reviewsImagesSliderStore.setStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap((v) => {
                this.logger.debug(
                    "ReviewsService.getReviewsImagesSlider is ready",
                    reqParams
                );
                applyTransaction(() => {
                    this.reviewsImagesSliderStore.set(v.items);
                    this.reviewsImagesSliderStore.update(() => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                        },
                    }));
                    this.reviewsImagesSliderStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    loadMoreReviewsImagesSlider(
        reqParams: GetReviewsParamsDTO
    ): Observable<ReviewsListResponseEntity> {
        this.reviewsImagesSliderStore.setStatus(STATUS.LOADING);

        return this.getReviewsRequest(reqParams).pipe(
            map((v) => create(v.body, ReviewsListResponseSchema)),
            catchError((error: unknown) => {
                this.logger.debug(
                    "ReviewsService.loadMoreReviewsImagesSlider is errored",
                    reqParams
                );
                applyTransaction(() => {
                    this.reviewsImagesSliderStore.setError(error);
                    this.reviewsImagesSliderStore.setStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap((v) => {
                this.logger.debug(
                    "ReviewsService.loadMoreReviewsImagesSlider is ready",
                    reqParams
                );
                applyTransaction(() => {
                    this.reviewsImagesSliderStore.add(v.items);
                    this.reviewsImagesSliderStore.update(() => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                        },
                    }));

                    this.reviewsImagesSliderStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    // Update Image Slider by review object
    updateImageSlider(review: ReviewEntity) {
        if (review.isPublished) {
            this.logger.debug("ReviewsService.updateImageSilder is update");
            this.reviewsImagesSliderStore.update(review.id, review);
        } else {
            this.logger.debug("ReviewsService.updateImageSilder is remove");
            this.reviewsImagesSliderStore.remove(review.id);
        }
    }
}

export default ReviewsImagesSliderService;
